import { useState, useEffect } from "react";
import { X, Search, Link2, Merge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "../api.js";
import PatientMergePanel from "./PatientMergePanel.jsx";
import { formatRecordDate } from "./YClientsRecordPickerModal.jsx";

// Привязка пациента к клиенту YClients прямо из карточки (только central-admin) — тот же механизм,
// что в «Разобрать»: поиск по телефону во всех филиалах, link-yclients, а при «клиент уже привязан
// к другому пациенту» — склейка.
export function YclientsLinkField({ patient, canManage, onLinked, onMerged }) {
  const [client, setClient] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!canManage || !patient.yclientsClientId || !patient.yclientsCompanyId) return;
    let cancelled = false;
    const clientId = String(patient.yclientsClientId);
    api.getYclientsClientAdmin(clientId, patient.yclientsCompanyId)
      .then(c => { if (!cancelled) setClient({ ...c, forId: clientId }); })
      .catch(() => { if (!cancelled) setClient({ error: true, forId: clientId }); });
    return () => { cancelled = true; };
  }, [canManage, patient.yclientsClientId, patient.yclientsCompanyId]);

  const linked = !!patient.yclientsClientId;
  const current = client && client.forId === String(patient.yclientsClientId) && !client.error ? client : null;
  const details = current ? [current.name, current.locationName].filter(Boolean).join(" · ") : null;

  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-slate-400 w-28 flex-shrink-0">YClients</span>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        {linked ? (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
            <Link2 className="h-3.5 w-3.5" />
            Привязан{details ? `: ${details}` : ""}
          </span>
        ) : (
          <span className="text-sm text-amber-600">Не привязан</span>
        )}
        {canManage && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowModal(true)}>
            {linked ? "Изменить" : "Привязать"}
          </Button>
        )}
      </div>
      {showModal && (
        <YclientsLinkModal
          patient={patient}
          onClose={() => setShowModal(false)}
          onLinked={(updated) => { setShowModal(false); onLinked(updated); }}
          onMerged={(survivorId) => { setShowModal(false); onMerged(survivorId); }}
        />
      )}
    </div>
  );
}

function YclientsLinkModal({ patient, onClose, onLinked, onMerged }) {
  const [phone, setPhone] = useState(patient.phone || "");
  const [matches, setMatches] = useState(patient.phone ? null : undefined); // undefined — ещё не искали, null — ищем
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");
  const [conflictPatient, setConflictPatient] = useState(null);
  const [merging, setMerging] = useState(false);

  const search = async () => {
    if (!phone.trim()) return;
    setMatches(null);
    setError("");
    try {
      const r = await api.searchYclientsClientAdmin(phone.trim());
      setMatches(r.matches || []);
    } catch (err) {
      setMatches([]);
      setError(err.response?.data?.error || "Не удалось выполнить поиск");
    }
  };

  useEffect(() => {
    if (!patient.phone) return;
    let cancelled = false;
    api.searchYclientsClientAdmin(patient.phone)
      .then(r => { if (!cancelled) setMatches(r.matches || []); })
      .catch(err => { if (!cancelled) { setMatches([]); setError(err.response?.data?.error || "Не удалось выполнить поиск"); } });
    return () => { cancelled = true; };
  }, [patient.phone]);

  const link = async (c) => {
    if (patient.yclientsClientId && String(patient.yclientsClientId) === String(c.id)) { onClose(); return; }
    setLinking(true);
    setError("");
    setConflictPatient(null);
    try {
      const updated = await api.linkYclientsPatient(patient.id, c.id, c.companyId);
      onLinked(updated);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.conflictPatient) setConflictPatient(err.response.data.conflictPatient);
      else setError(err.response?.data?.error || "Не удалось привязать");
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4" onMouseDown={e => e.target === e.currentTarget && !merging && onClose()}>
      <div className={`bg-white rounded-2xl shadow-2xl ${merging ? "w-[1100px]" : "w-[520px]"} max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">
            {merging ? "Склейка пациентов" : `Привязка к клиенту YClients — «${patient.name}»`}
          </span>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7"><X className="h-4 w-4" /></Button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-3 overflow-y-auto">
          {merging ? (
            <PatientMergePanel
              firstId={patient.id}
              secondId={conflictPatient.id}
              showToast={setError}
              onCancel={() => setMerging(false)}
              onDone={onMerged}
            />
          ) : (
            <>
              {patient.yclientsClientId && (
                <p className="text-xs text-slate-500">
                  Пациент уже привязан к клиенту #{patient.yclientsClientId}. Выбор другого клиента заменит привязку.
                </p>
              )}
              <div className="flex gap-2">
                <Input
                  className="h-8 text-xs"
                  value={phone}
                  placeholder="Телефон клиента"
                  onChange={e => setPhone(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && search()}
                />
                <Button size="sm" className="gap-1.5" disabled={!phone.trim() || matches === null} onClick={search}>
                  <Search className="h-3.5 w-3.5" /> Найти
                </Button>
              </div>

              {conflictPatient && (
                <div className="rounded-xl bg-amber-50 p-3 flex flex-col gap-2">
                  <p className="text-xs text-amber-800">
                    Этот клиент YClients уже привязан к пациенту «{conflictPatient.name}» ({conflictPatient.phone || "без телефона"}).
                    Если это один человек — склейте карточки, отметив, какие обследования и покупки оставить.
                  </p>
                  <Button size="sm" className="gap-1.5 w-fit" onClick={() => setMerging(true)}>
                    <Merge className="h-3.5 w-3.5" /> Склеить пациентов
                  </Button>
                </div>
              )}

              {error && <p className="text-xs text-red-600">{error}</p>}

              {matches === null && <p className="text-xs text-slate-400 text-center py-4">Поиск по всем филиалам...</p>}
              {Array.isArray(matches) && matches.length === 0 && !error && (
                <p className="text-xs text-slate-400 text-center py-4">Совпадений не найдено</p>
              )}
              {Array.isArray(matches) && matches.map(c => {
                const current = patient.yclientsClientId && String(patient.yclientsClientId) === String(c.id);
                return (
                  <button
                    key={`${c.locationId}-${c.id}`}
                    disabled={linking}
                    onClick={() => link(c)}
                    className="text-left rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors px-4 py-3 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                      <p className="text-xs text-slate-400">{c.phone}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {current && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">текущий</span>}
                      <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">{c.locationName}</span>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Ячейка «YClients» в таблице обследований: к какой записи привязан скан, пропущен ли он, и
// (для central-admin у привязанного пациента) кнопки «Привязать» / «Пропустить» / «Вернуть».
export function ScanRecordCell({ scan, records, canManage, patientLinked, onPickRecord, onSetSkipped }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const record = scan.yclientsRecordId ? records?.find(r => String(r.id) === String(scan.yclientsRecordId)) : null;

  if (scan.yclientsRecordId) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-emerald-700 whitespace-nowrap" title={`Запись #${scan.yclientsRecordId}`}>
          {record ? `${formatRecordDate(record.date)}${record.staffName ? ` · ${record.staffName}` : ""}` : `Запись #${scan.yclientsRecordId}`}
        </span>
        {canManage && patientLinked && (
          <button className="text-[11px] text-slate-400 hover:text-blue-600" onClick={stop(() => onPickRecord(scan))}>изменить</button>
        )}
      </div>
    );
  }
  if (scan.yclientsRecordSkippedAt) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-slate-400">Пропущено</span>
        {canManage && (
          <button className="text-[11px] text-slate-400 hover:text-blue-600" onClick={stop(() => onSetSkipped(scan, false))}>вернуть</button>
        )}
      </div>
    );
  }
  if (!canManage) return <span className="text-xs text-amber-600">Без записи</span>;
  if (!patientLinked) return <span className="text-xs text-slate-300" title="Сначала привяжите пациента к клиенту YClients">—</span>;
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={stop(() => onPickRecord(scan))}>Привязать</Button>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-slate-500" onClick={stop(() => onSetSkipped(scan, true))}>Пропустить</Button>
    </div>
  );
}
